import React, { useCallback, useEffect, useMemo, useState } from 'react';
import deepEqual from 'fast-deep-equal/es6/react';
import { useSelector } from 'react-redux';
import { getIDFromUrl } from '@rossum/api-client';
import { SchemaFieldIconType } from '@rossum/components/schema-field-icon/SchemaFieldIcon';
import { IconCustomMenuDown } from '@rossum/ui/icons/tabler';
import { ListItemProps, Skeleton, Stack, SvgIcon } from '@rossum/ui/material';
import { sidebarFieldIdsEnabledSelector } from '../../../../redux/modules/user/selectors';
import { Annotation } from '../../../../types/annotation';
import { EnumOption } from '../../../../types/schema';
import { SidebarItemLabel, SidebarItemLayout } from '../../../document-sidebar';
import { SidebarEnumFieldModel } from '../useSidebarData';
import { InactiveValue } from './shared/InactiveValue';
import { isMemoryValueSource } from './shared/isMemoryValueSource';
import { ItemContextActions } from './shared/ItemContextActions';
import { MagicContainer } from './shared/MagicContainer';
import { MessageIndicator } from './shared/MessageIndicator';
import { Messages } from './shared/Messages';
import { SidebarItemActiveEnumValue } from './shared/SidebarItemActiveEnumValue';
import { formatConfidenceScore, getFieldsSettingsPath } from './utils';

type EnumItemProps = Omit<ListItemProps, 'onChange' | 'onClick'> & {
  sidebarScrollableRef: HTMLDivElement | null;
  annotation: Annotation | undefined;
  item: SidebarEnumFieldModel;
  active: boolean;
  onChange: (
    item: SidebarEnumFieldModel,
    newValue: string,
    struct?: unknown
  ) => void;
  onClick: (datapointId: number) => void;
  loading: boolean;
  selected?: boolean;
  disabled: boolean;
  ghost?: boolean;
};

const EnumItem = React.memo(
  ({
    sidebarScrollableRef,
    annotation,
    item,
    active,
    onChange,
    onClick,
    disabled,
    selected,
    loading,
    ghost,
    ...listItemProps
  }: EnumItemProps) => {
    const handleChange = useCallback(
      (newValue: EnumOption | null) => {
        onChange(item, newValue?.value ?? '', newValue?.struct);
      },
      [item, onChange]
    );

    const handleClick = useCallback(() => {
      onClick(item.id);
    }, [item.id, onClick]);

    const isDisabled = disabled || item.editing === 'disabled';

    const isEdited = !!('noRecalculation' in item && item.noRecalculation);

    const isConfirmed = useMemo(() => {
      return item.validationSources.includes('human');
    }, [item.validationSources]);

    const iconType: SchemaFieldIconType = item.valueSource;

    const isMemory = isMemoryValueSource(item.struct);

    const iconVariant = useMemo(() => {
      if (isEdited) {
        return 'edited';
      }

      if (isMemory) {
        return 'saved';
      }

      if (isConfirmed) {
        return 'confirmed';
      }

      return 'default';
    }, [isConfirmed, isEdited, isMemory]);

    const iconColor = useMemo(() => {
      if (isMemory) {
        return 'aurora' as const;
      }

      if (isEdited || isConfirmed) {
        return 'success' as const;
      }

      return 'inherit' as const;
    }, [isConfirmed, isEdited, isMemory]);

    const sidebarAdditionalInfo = useSelector(sidebarFieldIdsEnabledSelector);

    const queueId = annotation ? getIDFromUrl(annotation?.queue) : undefined;

    // Auto-opening of dropdown
    const [shouldAutoOpen, setShouldAutoOpen] = useState(false);

    const handleShouldAutoOpen = useCallback(() => {
      setShouldAutoOpen(true);
    }, []);

    useEffect(() => {
      if (!active) {
        setShouldAutoOpen(false);
      }
    }, [active]);

    return (
      <MagicContainer
        active={active}
        sidebarScrollableRef={sidebarScrollableRef}
        onClick={active ? undefined : handleClick}
        selected={selected}
        disabled={isDisabled}
        ghost={ghost}
        {...listItemProps}
      >
        <SidebarItemLayout
          iconSlot={
            <ItemContextActions
              item={item}
              active={active}
              disabled={isDisabled}
              iconProps={{
                type: iconType,
                variant: iconVariant,
                color: iconColor,
              }}
              fieldSettingsPath={
                queueId ? getFieldsSettingsPath(queueId, item.schemaId) : null
              }
            />
          }
          labelSlot={
            <SidebarItemLabel
              label={item.label}
              sublabel={sidebarAdditionalInfo ? item.schemaId : undefined}
              isRequired={item.required}
            />
          }
          valueSlot={
            loading ? (
              <Stack height={1} justifyContent="center">
                <Skeleton height="100%" />
              </Stack>
            ) : active ? (
              <SidebarItemActiveEnumValue
                shouldAutoOpen={shouldAutoOpen}
                value={item.valueOption}
                onChange={handleChange}
                options={item.options}
                clearable={item.clearable}
                disabled={isDisabled}
              />
            ) : (
              <InactiveValue
                value={item.valueOption?.label ?? ''}
                confidence={formatConfidenceScore(item.confidenceScore)}
                onClick={handleShouldAutoOpen}
              />
            )
          }
          decorationSlot={
            active ? null : (
              <Stack
                direction="row"
                spacing={0.5}
                onClick={handleShouldAutoOpen}
              >
                <MessageIndicator messagesStats={item.messagesStats} />
                <SvgIcon
                  fontSize="large"
                  sx={{ marginTop: '-2px', marginRight: '4px' }}
                  color={isDisabled ? 'disabled' : 'secondary'}
                >
                  <IconCustomMenuDown />
                </SvgIcon>
              </Stack>
            )
          }
        />
        {active && item.messages.length > 0 && (
          <Messages messages={item.messages} />
        )}
      </MagicContainer>
    );
  },
  (prev, next) => {
    return deepEqual(prev, next);
  }
);

EnumItem.displayName = 'EnumItem';

export { EnumItem };
